import React, {useEffect, useState} from 'react'
import {Box, Stack, Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import MultiSelectNew from 'src/components-new/inputs/MultiSelectNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {DocRecord} from './columns'

interface CategoryOption {
  value: string
  label: string
}

interface DocumentInfoDialogNewProps {
  doc: DocRecord
  open: boolean
  onClose: () => void
  onSave: (id: string, payload: {category: string[]; name: string; description: string}) => void
  readOnly: boolean
  categoryOptions: CategoryOption[]
  onError?: () => void
}

function StaticField({label, value}: {label: string; value: string}) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" sx={{color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600}}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{color: NEUTRAL_SCALE[900], fontWeight: 400}}>
        {value || '—'}
      </Typography>
    </Stack>
  )
}

export default function DocumentInfoDialogNew({
  doc,
  open,
  onClose,
  onSave,
  readOnly,
  categoryOptions,
}: DocumentInfoDialogNewProps): JSX.Element {
  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [category, setCategory] = useState<string[]>([])

  useEffect(() => {
    if (doc) {
      setName(doc.name ?? doc.fileName ?? '')
      setDescription(doc.description ?? '')
      setCategory(doc.category ?? [])
    }
  }, [doc])

  const sizeLabel = doc?.size
    ? `${(doc.size / 1024 ** 2).toFixed(2)} MB`
    : '—'

  const categoryLabel = category
    .map(v => categoryOptions.find(o => o.value === v)?.label ?? v)
    .join(', ')

  const actions = (
    <Box sx={{display: 'flex', gap: 1}}>
      <Button variant="secondary" onClick={onClose}>
        {readOnly ? 'Chiudi' : 'Annulla'}
      </Button>
      {!readOnly && (
        <Button
          variant="primary"
          disabled={!category.length}
          onClick={() => onSave(doc._id, {category, name: name || '', description: description || ''})}
        >
          Salva
        </Button>
      )}
    </Box>
  )

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={doc?.name || doc?.fileName || 'Documento'}
      actions={actions}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        {readOnly ? (
          <StaticField label="Nome" value={name} />
        ) : (
          <TextFieldNew label="Nome" value={name} onChange={setName} fullWidth />
        )}

        {readOnly ? (
          <StaticField label="Descrizione" value={description} />
        ) : (
          <TextAreaNew label="Descrizione" value={description} onChange={setDescription} fullWidth />
        )}

        <StaticField label="Tipo Documento" value={doc?.type ?? ''} />

        {readOnly ? (
          <StaticField label="Categoria" value={categoryLabel} />
        ) : (
          <MultiSelectNew
            label="Categoria"
            value={category}
            onChange={setCategory}
            options={categoryOptions}
            required
            fullWidth
          />
        )}

        <StaticField label="Nome file" value={doc?.fileName ?? ''} />
        <StaticField label="Dimensione" value={sizeLabel} />
      </Box>
    </AppDialog>
  )
}
